import React, { useState } from 'react'
import styled from 'styled-components'
import { Spin, Button, Input, Space, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { refineOverallSummary, updateOverallSummary } from '../../api/adaptiveCoach'
const { TextArea } = Input

const Container = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  min-height: 250px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #383e48;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
  }

  .sleep-summary {
    .summary-content {
      color: #6d7d8c;
      line-height: 1.6;
    }

    .summary-bullets {
      margin-top: 12px;
      padding-left: 20px;

      li {
        margin-bottom: 8px;
        color: #6d7d8c;
        font-size: 14px;
      }
    }

    .insight-section {
      h4 {
        margin-bottom: 8px;
        color: #383e48;
        font-size: 14px;
        font-weight: 600;
      }

      p {
        margin-bottom: 16px;
        font-size: 14px;
      }
    }

    .divider {
      height: 1px;
      background: #e4e7eb;
      margin: 20px 0;
    }
  }

  .no-data {
    text-align: center;
    color: #9aa5b1;
    padding: 82px 0;
    font-style: italic;
  }

  .refine-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid #e4e7eb;
  }

  .refine-panel {
    margin-top: 12px;
    padding: 12px;
    background: #f7f9fa;
    border-radius: 6px;
    border: 1px solid #e4e7eb;

    .refine-prompt-label {
      font-size: 13px;
      color: #383e48;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .refine-buttons {
      margin-top: 8px;
      display: flex;
      gap: 8px;
    }

    .editable-summary {
      margin-bottom: 12px;
      padding: 12px;
      background: #ffffff;
      border-radius: 6px;
      border: 1px solid #e4e7eb;

      .editable-header {
        font-size: 13px;
        color: #383e48;
        font-weight: 500;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .summary-editor {
        .ant-input {
          border-radius: 4px;
        }
      }
    }
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd2d9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9aa5b1;
  }
`

const ClientInsights = ({ sleepSummary, loading, personId, recordDate, onSummaryUpdated, overall_summary }) => {
  const [showRefinePanel, setShowRefinePanel] = useState(false)
  const [refinePrompt, setRefinePrompt] = useState('')
  const [refining, setRefining] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [editorObj, setEditorObj] = useState({ whats_working_well: '', areas_for_attention: '', action_plan: '' })
  const [refinedObj, setRefinedObj] = useState(null)
  const [showComparison, setShowComparison] = useState(false)

  const toPlainText = (data) => {
    const d = data || {}
    return [
      d.whats_working_well || '',
      d.areas_for_attention || '',
      d.action_plan || ''
    ].join('\n\n')
  }

  const fromPlainText = (text) => {
    const parts = text.split(/\n\n|\n/).map((p) => p.trim())
    const obj = { whats_working_well: '', areas_for_attention: '', action_plan: '' }
    const order = ['whats_working_well', 'areas_for_attention', 'action_plan']
    parts.forEach((part, idx) => {
      if (idx < 3) obj[order[idx]] = part
    })
    return obj
  }

  const summaryData = (s) => (s ? s.overall_summary || s : {})

  const renderSleepSummary = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Spin size="small" />
        </div>
      )
    }

    const summaryData =
      overall_summary ||
      (sleepSummary && sleepSummary.overall_summary) ||
      sleepSummary

    if (!summaryData || Object.keys(summaryData).length === 0) {
      return <div className="no-data">No summary available</div>
    }

    return (
      <div className="sleep-summary">
        <div className="summary-content">
          <ul className="summary-bullets">
            {summaryData.whats_working_well && (
              <li className="insight-section">{summaryData.whats_working_well}</li>
            )}
            {summaryData.areas_for_attention && (
              <li className="insight-section">{summaryData.areas_for_attention}</li>
            )}
            {summaryData.action_plan && (
              <li className="insight-section">{summaryData.action_plan}</li>
            )}
          </ul>
        </div>
      </div>
    )
  }

  const handleRefineClick = () => {
    const next = !showRefinePanel
    setShowRefinePanel(next)
    setShowComparison(false)
    if (next) {
      const data = summaryData(sleepSummary)
      setEditorObj({ 
        whats_working_well: data.whats_working_well || '', 
        areas_for_attention: data.areas_for_attention || '', 
        action_plan: data.action_plan || '' 
      })
      setRefinedObj(null)
      setRefinePrompt('')
    }
  }

  const handleRefine = async () => {
    if (!refinePrompt.trim()) {
      message.warning('Please enter a refinement prompt')
      return
    }
    if (!personId) {
      message.error('Person ID not available')
      return
    }

    setRefining(true)
    try {
      const summaryObj = editorObj
      const response = await refineOverallSummary(personId, recordDate, summaryObj, refinePrompt)
      if (response && (response.refined_summary || response.whats_working_well || response.areas_for_attention || response.action_plan)) {
        const refined = {
          whats_working_well: (response.refined_summary && response.refined_summary.whats_working_well) || response.whats_working_well || '',
          areas_for_attention: (response.refined_summary && response.refined_summary.areas_for_attention) || response.areas_for_attention || '',
          action_plan: (response.refined_summary && response.refined_summary.action_plan) || response.action_plan || ''
        }
        setRefinedObj(refined)
        setShowComparison(true)
      } else {
        message.error('Failed to refine summary')
      }
    } catch (error) {
      console.error('Error refining summary:', error)
      message.error('Failed to refine summary')
    } finally {
      setRefining(false)
    }
  }

  const handleAccept = () => {
    setEditorObj(refinedObj || editorObj)
    setRefinedObj(null)
    setRefinePrompt('')
    setShowComparison(false)
  }

  const handleDecline = () => {
    setRefinedObj(null)
    setRefinePrompt('')
    setShowComparison(false)
  }

  const handleSave = async () => {
    if (!personId) {
      message.error('Person ID not available')
      return
    }
    setUpdating(true)
    try {
      await updateOverallSummary(personId, recordDate, editorObj)
      message.success('Summary updated successfully')
      setShowRefinePanel(false)
      setEditorObj({ whats_working_well: '', areas_for_attention: '', action_plan: '' })
      setRefinedObj(null)
      setRefinePrompt('')
      if (typeof onSummaryUpdated === 'function') {
        onSummaryUpdated()
      }
    } catch (error) {
      console.error('Error updating summary:', error)
      message.error('Failed to update summary')
    } finally {
      setUpdating(false)
    }
  }

  const handleCancel = () => {
    setShowRefinePanel(false)
    setEditorObj({ whats_working_well: '', areas_for_attention: '', action_plan: '' })
    setRefinedObj(null)
    setRefinePrompt('')
    message.info('Changes discarded')
  }

  return (
    <Container>
      {!showRefinePanel && (
        <>
          <div className="section-title">
            <span>Key Insights</span>
            {!loading && sleepSummary && Object.keys(sleepSummary).length > 0 && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={handleRefineClick}
                size="small"
                style={{ color: '#2B4B8F'}}
              >
                Refine with AI
              </Button>
            )}
          </div>
          {renderSleepSummary()}
        </>
      )}

      {showRefinePanel && !loading && (
        <div className="refine-panel">
          {!showComparison && (
            <div className="refine-buttons" style={{ marginBottom: 12 }}>
              <Space>
                <Button onClick={handleSave} loading={updating}>
                  Save
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            </div>
          )}

          {!showComparison ? (
            <>
              <div className="editable-summary">
                <div className="summary-editor">
                  <TextArea
                    rows={8}
                    value={toPlainText(editorObj)}
                    onChange={(e) => setEditorObj(fromPlainText(e.target.value))}
                    placeholder="Edit summary..."
                  />
                </div>
              </div>

              <div className="refine-prompt-label">Refinement Prompt</div>
              <TextArea
                rows={2}
                placeholder="E.g., Make it more concise, focus on sleep patterns..."
                value={refinePrompt}
                onChange={(e) => setRefinePrompt(e.target.value)}
                disabled={refining}
              />
              <div className="refine-buttons">
                <Button type="primary" onClick={handleRefine} loading={refining} icon={<EditOutlined />}>
                  Refine
                </Button>
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: 12, border: '1px solid #e4e7eb', borderRadius: 6, backgroundColor: '#fff', marginBottom: 12, marginTop: 12 }}>
                <div style={{ fontSize: 13, color: '#383e48', fontWeight: 500, marginBottom: 8 }}>Current Summary</div>
                <div style={{ whiteSpace: 'pre-wrap', padding: '8px 0' }}>{toPlainText(editorObj)}</div>
              </div>
              <div style={{ padding: 12, border: '1px solid #e4e7eb', borderRadius: 6, backgroundColor: '#fff' }}>
                <div style={{ fontSize: 13, color: '#383e48', fontWeight: 500, marginBottom: 8 }}>Refined Summary</div>
                <div style={{ whiteSpace: 'pre-wrap', padding: '8px 0' }}>{refinedObj ? toPlainText(refinedObj) : ''}</div>
                <div className="refine-buttons">
                  <Space>
                    <Button type="primary" onClick={handleAccept}>Accept</Button>
                    <Button onClick={handleDecline}>Decline</Button>
                  </Space>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Container>
  )
}

export default ClientInsights
